import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type { AccountWorkspace } from '@shared/flow/account-workspaces';
import { EmDash, fmtDate } from '../cells';
import { CompactUrlLink } from '../links';
import { useAccountWorkspaces } from './hooks';
import { wsAppUrl, displayHost, yesNo, chatGptText, WS_COLS, workspaceCountsLabel } from './accountWorkspacesPresentation';

/**
 * Bucket E Workspaces accordion (U6) — a collapsed-by-default section
 * between the drill-down's quads and the Contacts roster
 * (`docs/flow/bucket-a-account-view-prototype.html` :409, :948-1011),
 * showing the account's `account_activity` workspace rows split into
 * Paid/Trial tables.
 *
 * Trial-table overflow (plan's flagged decision): the prototype shows
 * "Showing N of M · View All" with a dead link; this renders every row the
 * backend returns instead — the accordion is collapsed by default, so the
 * cost of an unbounded render is paid only when a rep opens it, and a real
 * expand affordance would be more code for no behavioral gain (the backend
 * already caps at 2000 rows, `src/api/account-workspaces.ts`).
 */

/** Strip the scheme for the plain-text "Workspace URL" column. */
function displayUrl(url: string | null): React.ReactNode {
  const host = displayHost(url);
  if (!host) return <EmDash />;
  return <span className="font-mono text-[11px]">{host}</span>;
}

const TH_CLASS =
  'border-b border-secondary dark:border-nightshade-700 px-2 py-1 text-left font-mono text-[10px] font-medium uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 whitespace-nowrap';
const TD_CLASS = 'border-b border-secondary/60 dark:border-nightshade-800 px-2 py-1.5 text-[11.5px] text-content-primary dark:text-nightshade-100 whitespace-nowrap';

function WorkspaceRow({ ws }: { ws: AccountWorkspace }) {
  const url = wsAppUrl(ws.workspaceId) ?? ws.workspaceUrl;
  // hasChatGpt is a SF checkbox (never null); chatGptConnectionDate is only
  // ever set when hasChatGpt is true — nothing to show on the false branch
  // (prototype:961-963).
  const chatGpt = ws.hasChatGpt ? chatGptText(true, ws.chatGptConnectionDate) : <EmDash />;

  return (
    <tr>
      <td className={TD_CLASS}>
        <CompactUrlLink url={url} label={ws.workspaceName ?? '—'} />
      </td>
      <td className={TD_CLASS}>{displayUrl(url)}</td>
      <td className={TD_CLASS}>{ws.productTier ?? <EmDash />}</td>
      <td className={TD_CLASS}>{fmtDate(ws.lastTrialStartDate)}</td>
      <td className={TD_CLASS}>{fmtDate(ws.lastTrialEndDate)}</td>
      <td className={TD_CLASS}>{yesNo(ws.activeTrial)}</td>
      <td className={TD_CLASS}>{ws.activeStatus ?? <EmDash />}</td>
      <td className={TD_CLASS}>{ws.creatorName ?? <EmDash />}</td>
      <td className={TD_CLASS}>{chatGpt}</td>
    </tr>
  );
}

/** Spanning "<Label> Workspaces (N)" section-header row, styled after the
 * timeline's sticky bucket headers (`AccountTimeline.tsx` bucketLabel row) —
 * the visual precedent for a full-width divider inside a shared table. */
const SECTION_HEADER_CLASS =
  'bg-bg-secondary/60 dark:bg-nightshade-800/60 px-2 py-1 text-left text-[10.5px] uppercase tracking-wide font-medium text-content-tertiary dark:text-nightshade-500';

/** One Paid/Trial section's rows: a spanning "<Label> Workspaces (N)" header
 * row, then either the data rows or a spanning "None." row — matches the
 * prototype's always-visible header-with-counts treatment. Rendered inside
 * the parent's single shared `<table>` so Paid/Trial columns share one
 * column-width template (R10) instead of auto-sizing independently. */
function WorkspaceSectionRows({ label, rows }: { label: string; rows: AccountWorkspace[] }) {
  return (
    <>
      <tr>
        <th colSpan={WS_COLS.length} className={SECTION_HEADER_CLASS}>
          {label} ({rows.length})
        </th>
      </tr>
      {rows.length === 0 ? (
        <tr>
          <td colSpan={WS_COLS.length} className="px-2 py-1.5 text-[11.5px] italic text-content-tertiary dark:text-nightshade-500">
            None.
          </td>
        </tr>
      ) : (
        rows.map(ws => <WorkspaceRow key={ws.id} ws={ws} />)
      )}
    </>
  );
}

/** Collapsed-by-default Workspaces accordion (Bucket E U6). Always mounted
 * between the quads and Contacts — never hidden, even with zero workspaces
 * (follow prototype: the header always shows real counts). */
export function AccountWorkspacesSection({ accountId, onSeeClayUsers }: { accountId: string; onSeeClayUsers?: () => void }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useAccountWorkspaces(accountId);

  // Defensive optional-chaining, not just belt-and-suspenders: `validateResponse`
  // WARNS-AND-RETURNS-RAW on a schema mismatch rather than throwing (see its
  // doc comment), so a malformed/absent-field payload must not crash the
  // collapsed header's count line.
  const paidRows = data?.paid ?? [];
  const trialRows = data?.trial ?? [];
  const counts = data ? ` ${workspaceCountsLabel(data)}` : '';

  return (
    <div data-testid="account-workspaces-section">
      <div className="flex items-center justify-between gap-1.5">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          className="flex flex-1 items-center gap-1.5 rounded-md py-1.5 text-left text-[13px] font-semibold text-content-primary dark:text-nightshade-100 hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60"
        >
          {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
          <span>
            Workspaces
            <span className="font-normal text-content-tertiary dark:text-nightshade-500">{counts}</span>
          </span>
        </button>
        {onSeeClayUsers && (
          <button
            type="button"
            onClick={onSeeClayUsers}
            className="shrink-0 text-[11.5px] font-medium text-content-action dark:text-blueberry-400 hover:underline"
          >
            See Clay Users →
          </button>
        )}
      </div>

      {open && (
        <div className="bg-white dark:bg-nightshade-900 border border-secondary dark:border-nightshade-700 rounded-lg px-3 py-3">
          {isLoading && <div className="text-[12px] text-content-tertiary dark:text-nightshade-500 py-2">Loading workspaces…</div>}
          {error && <div className="text-[12px] text-content-danger dark:text-red-400 py-2">Workspaces failed to load.</div>}
          {data && !data.hasAccess && (
            <div className="text-[12px] text-content-tertiary dark:text-nightshade-500 py-2">No workspaces access for this account.</div>
          )}
          {data && data.hasAccess && (
            <div className="overflow-x-auto rounded-md border border-secondary dark:border-nightshade-700">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {WS_COLS.map(col => <th key={col} className={TH_CLASS}>{col}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <WorkspaceSectionRows label="Paid Workspaces" rows={paidRows} />
                  <WorkspaceSectionRows label="Trial Workspaces" rows={trialRows} />
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
